import {VIEW_MODES} from '../../constants/editorConfig'
import {bindNodeInput} from './input-binder/bindNodeInput'
import {loadImage} from './helpers/cache'
import {createKonvaNode, getEffectiveStyle} from './nodeFactory'

function isVisibleInMode(model, viewMode) {
    const modes = model.style?.visibleInModes
    if (!modes || !Array.isArray(modes)) return true
    return modes.includes(viewMode)
}

function attrsEqual(a, b) {
    if (a === b) return true
    if (!a || !b) return false
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const k of keys) {
        if (a[k] !== b[k]) return false
    }
    return true
}

export class KonvaAdapter {
    constructor({
        stage,
        layer,
        editor,
        interaction,
        renderProfile = VIEW_MODES.OVERLAY,
        dragLayer = null,
        onStartTextEditing,
        runtime,
    }) {
        this.stage = stage
        this.layer = layer
        this.editor = editor
        this.interaction = interaction
        this.renderProfile = renderProfile
        this.dragLayer = dragLayer
        this.onStartTextEditing = onStartTextEditing
        this.runtime = runtime

        this.nodes = new Map()
        this.unsubscribe = null
        this.unsubscribeUiState = null
    }

    mount() {
        if (this.unsubscribe) return

        this.sync(this.editor.getState())

        this.unsubscribe = this.editor.subscribe((state) => {
            this.sync(state)
        })

        if (this.runtime?.subscribeUiState) {
            this.unsubscribeUiState = this.runtime.subscribeUiState(() => {
                this.sync(this.editor.getState())
            })
        }
    }

    unmount() {
        this.unsubscribe?.()
        this.unsubscribe = null
        this.unsubscribeUiState?.()
        this.unsubscribeUiState = null

        for (const {node, unbindNodeInput} of this.nodes.values()) {
            unbindNodeInput?.()
            node?.destroy()
        }
        this.nodes.clear()
    }

    sync(state) {
        // Apply viewport once (shared stage); overlay adapter owns it
        if (this.renderProfile === VIEW_MODES.OVERLAY) {
            this.applyViewport(state.viewport)
        }

        state.nodes.forEach((model) => {
            if (!isVisibleInMode(model, this.renderProfile)) return

            if (!this.nodes.has(model.id)) {
                const node = createKonvaNode(model, this.renderProfile)
                if (node) {
                    this.layer.add(node)
                    const bindOptions = this.dragLayer ? {mainLayer: this.layer, dragLayer: this.dragLayer} : undefined
                    const unbindNodeInput = bindNodeInput(
                        node,
                        this.editor,
                        this.interaction,
                        bindOptions,
                        this.onStartTextEditing,
                        this.runtime,
                    )
                    const effectiveForEntry = getEffectiveStyle(model, this.renderProfile)
                    const srcForEntry = model.content?.src ?? effectiveForEntry.src
                    const lastImageSrc = model.type === 'image' && srcForEntry ? srcForEntry : undefined
                    this.nodes.set(model.id, {node, unbindNodeInput, lastAttrs: null, lastImageSrc})
                    this.updateNode(model)
                }
            } else {
                this.updateNode(model)
            }
        })

        for (const [id, {node, unbindNodeInput}] of this.nodes) {
            const model = state.nodes.get?.(id)
            if (!model || !isVisibleInMode(model, this.renderProfile)) {
                unbindNodeInput?.()
                node.destroy()
                this.nodes.delete(id)
            }
        }

        this.syncOrder(state.order)

        this.layer.batchDraw()
    }

    syncOrder(order) {
        if (!order || order.length === 0) return

        order.forEach((id, index) => {
            const {node} = this.nodes.get(id)
            if (!node) return
            node.zIndex(index)
        })
    }

    updateNode(model) {
        const entry = this.nodes.get(model.id)
        if (!entry?.node) return
        const {node, lastAttrs} = entry

        const g = model.geometry
        const effective = getEffectiveStyle(model, this.renderProfile)

        const imageSrc = model.content?.src ?? effective.src
        if (model.type === 'image' && imageSrc && imageSrc !== entry.lastImageSrc) {
            entry.lastImageSrc = imageSrc
            loadImage(imageSrc, (img) => {
                if (!entry.node.getLayer()) return
                entry.node.image(img)
                if (entry.node.clearCache) entry.node.clearCache()
                if (entry.node.cache) entry.node.cache()
                entry.node.getLayer().batchDraw()
            })
        }

        const editingNodeId = this.runtime?.getUiState?.().textEditing?.nodeId
        const isBeingEdited = model.type === 'text' && model.id === editingNodeId

        const attrs = {
            x: g.x,
            y: g.y,
            width: g.width,
            height: g.height,
            rotation: g.rotation,
            draggable: !model.state?.locked,
            visible: isBeingEdited ? false : !model.state?.hidden,
        }

        if (model.type === 'shape') {
            attrs.fill = effective.fill
            attrs.stroke = this.renderProfile === VIEW_MODES.SILHOUETTE ? '#000000' : effective.stroke
            attrs.strokeWidth = effective.strokeWidth ?? 0
        } else if (model.type === 'text') {
            attrs.text = model.content?.text ?? effective.text ?? ''
            attrs.fontSize = effective.fontSize ?? 16
            attrs.fontFamily = effective.fontFamily ?? 'Arial'
            attrs.fill = effective.fill ?? (this.renderProfile === VIEW_MODES.SILHOUETTE ? 'black' : '#000')
            attrs.align = effective.align ?? 'left'
            if (this.renderProfile === VIEW_MODES.SILHOUETTE) {
                attrs.stroke = '#000000'
                attrs.strokeWidth = effective.strokeWidth ?? 0
            }
        }

        if (lastAttrs != null && attrsEqual(lastAttrs, attrs)) {
            return
        }
        entry.lastAttrs = attrs
        if (model.type === 'image' && node.clearCache) {
            node.clearCache()
        }
        node.setAttrs(attrs)
        if (model.type === 'image' && node.cache) {
            node.cache()
        }
    }

    applyViewport(viewport) {
        const {scale, x, y} = viewport

        // scale canvas
        this.stage.scale({
            x: scale,
            y: scale,
        })

        // pan (translate)
        this.stage.position({
            x: -x * scale,
            y: -y * scale,
        })
    }
}
